import React from 'react';
import DataGrid from '#/common/components/DataGrid';
import columns from './columns';
import { StyledNuvoBox } from '#/common/styles/template';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';

const InfoLabel = styled(NuvoLabel)`
  color: ${color('primary', 'dark')};
  background-color: ${color('info', 'middle', 0.15)};
  border: 1px solid ${color('info', 'middle')} !important;
  border-radius: 20px;
  padding: 10px 20px;
  font-size: 14px;
  width: 100%;
`;

const DataGridWrapper = styled.div`
  margin-bottom: 16px;
`;

export interface PreviewStepProps {
  mapping: Record<string, string>;
  csvTopLines: string[][];
}

const PreviewStep: React.FC<PreviewStepProps> = ({ mapping, csvTopLines }) => {
  const { isDesktop } = useViewport();

  const columnsFields = Object.values(columns).map((col) => col.dataField);

  const dataSource = React.useMemo(() => {
    if (csvTopLines.length === 0) return [];

    const [headerRow, ...dataRows] = csvTopLines;

    // Create a reverse mapping: CSV column index -> field name
    const columnIndexToField: Record<number, string> = {};
    Object.entries(mapping).forEach(([fieldName, csvHeader]) => {
      const columnIndex = headerRow.indexOf(csvHeader);
      if (columnIndex !== -1) {
        columnIndexToField[columnIndex] = fieldName;
      }
    });

    // Transform each data row into an object using the mapping
    return dataRows.map((row) => {
      const rowData: Record<string, string> = {};
      row.forEach((cellValue, columnIndex) => {
        const fieldName = columnIndexToField[columnIndex];
        if (fieldName) {
          rowData[fieldName] = cellValue;
        }

        // Include the Status which is Installed by default
        rowData['status'] = 'Installed';

        // Fill unmapped columns with empty strings
        columnsFields.forEach((field) => {
          if (!(field in rowData)) {
            rowData[field] = 'Unknown';
          }
        });
      });
      return rowData;
    });
  }, [mapping, csvTopLines]);

  return (
    <StyledNuvoBox isDesktop={isDesktop}>
      <div className="box-caption">
        <NuvoLabel>Preview</NuvoLabel>
      </div>
      <InfoLabel>
        Here is a preview of how your new assets will look like in the Assets Table. You can go back
        and edit your columns if needed before submitting.
      </InfoLabel>
      <DataGridWrapper>
        <DataGrid
          id="data-import-preview"
          pluralText="Assets"
          singularText="Asset"
          selectionMode="none"
          actions={['column-chooser']}
          columns={columns}
          dataSource={dataSource}
          disablePaging={true}
          disableSorting={true}
          disableFiltering={true}
        />
      </DataGridWrapper>
    </StyledNuvoBox>
  );
};

export default React.memo(PreviewStep);
